import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { products } from "@/data/products";
import poster from "@/assets/ecosystem-poster.webp";
const suiteVideo = { url: "/videos/banetech-ecosystem-suite.mp4" };

// Each product's place in the rainforest analogy (layer + role copy).
const roles: Record<string, { layer: string; role: string; x: number; y: number }> = {
  "lemur-monitor": { layer: "Canopy", role: "Watches every signal from the top of the forest.", x: 300, y: 70 },
  "bee-portfolio": { layer: "Canopy", role: "Knows the map of the whole ecosystem.", x: 500, y: 95 },
  "gaia-entity": { layer: "Understory", role: "Correlates what happens across the ecosystem.", x: 400, y: 185 },
  firefly: { layer: "Understory", role: "Carries the light between systems.", x: 180, y: 200 },
  "koala-flow": { layer: "Understory", role: "Keeps order over who builds and changes what.", x: 620, y: 200 },
  fireant: { layer: "Forest floor", role: "Captures change at the roots, in the data itself.", x: 400, y: 300 },
};

const links: [string, string][] = [
  ["fireant", "firefly"], ["fireant", "gaia-entity"], ["firefly", "gaia-entity"], ["firefly", "lemur-monitor"],
  ["gaia-entity", "lemur-monitor"], ["gaia-entity", "bee-portfolio"], ["koala-flow", "bee-portfolio"],
  ["koala-flow", "gaia-entity"], ["fireant", "koala-flow"], ["lemur-monitor", "bee-portfolio"],
];

export const Ecosystem = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const items = products.filter((p) => roles[p.slug]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { rootMargin: "100px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (paused || !visible) return;
    const t = window.setInterval(() => setActive((i) => (i + 1) % items.length), 3500);
    return () => window.clearInterval(t);
  }, [paused, visible, items.length]);

  const current = items[active];
  const pos = (slug: string) => roles[slug];

  return (
    <section ref={sectionRef} id="ecosystem" className="relative py-24 md:py-32 bg-surface">
      <div className="container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">The Suite</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight">
              See and control your enterprise ecosystem.
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Like a rainforest, an enterprise is a living ecosystem. Each Bane Tech product plays its part, from the roots to the canopy.
            </p>
          </div>
        </div>

        <div
          className="grid lg:grid-cols-[1.6fr_1fr] gap-8 items-center"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="rounded-2xl border border-border bg-card p-4 shadow-[var(--shadow-card)]">
            <p className="flex items-center justify-center gap-2 mb-2 font-mono text-xs uppercase tracking-widest text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" aria-hidden="true" />
              Hover or tap any product to trace its connections
            </p>
            <svg viewBox="0 0 800 360" className="w-full h-auto" role="img" aria-label="Bane Tech product ecosystem">
              {[["Canopy", 80], ["Understory", 195], ["Forest floor", 300]].map(([label, y]) => (
                <text key={label as string} x="12" y={(y as number) + 4} className="fill-muted-foreground font-mono" fontSize="10">
                  {(label as string).toUpperCase()}
                </text>
              ))}
              {links.map(([a, b]) => {
                const on = current && (current.slug === a || current.slug === b);
                return (
                  <line
                    key={a + b}
                    x1={pos(a).x} y1={pos(a).y} x2={pos(b).x} y2={pos(b).y}
                    className={on ? "stroke-primary eco-flow" : "stroke-border"}
                    strokeWidth={on ? 2 : 1.25}
                    strokeDasharray={on ? "6 8" : undefined}
                  />
                );
              })}
              {items.map((p, i) => {
                const r = pos(p.slug);
                const on = i === active;
                return (
                  <g
                    key={p.slug}
                    transform={`translate(${r.x} ${r.y})`}
                    className="cursor-pointer"
                    onClick={() => setActive(i)}
                    onMouseEnter={() => setActive(i)}
                    tabIndex={0}
                    onFocus={() => setActive(i)}
                    role="button"
                    aria-label={p.name}
                  >
                    <g className="group/node transition-transform duration-200 ease-out hover:scale-110 focus-visible:scale-110">
                    {on && <circle r="38" className="fill-primary/10 eco-pulse" />}
                    <circle r="28" className={on ? "fill-primary stroke-primary-glow" : "fill-primary/70 stroke-card transition-colors group-hover/node:fill-primary"} strokeWidth="3" />
                    <image href={p.icon} x="-16" y="-16" width="32" height="32" />
                    <text y="48" textAnchor="middle" fontSize="12" className={on ? "fill-foreground font-semibold" : "fill-muted-foreground"}>
                      {p.name}
                    </text>
                    </g>
                  </g>
                );
              })}
            </svg>
          </div>

          {current && (
            <div key={current.slug} className="animate-fade-in">
              <p className="font-mono text-xs uppercase tracking-widest text-accent mb-3">{pos(current.slug).layer}</p>
              <div className="flex items-center gap-3 mb-3">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-[image:var(--gradient-primary)]"><img src={current.icon} alt="" className="h-7 w-7" /></span>
                <h3 className="font-display text-2xl font-bold">{current.name}</h3>
              </div>
              <p className="text-foreground font-medium">{current.tagline}</p>
              <p className="mt-2 text-muted-foreground">{pos(current.slug).role}</p>
              <Link to={`/products/${current.slug}/`} className="mt-5 inline-flex items-center gap-1 text-primary font-medium hover:underline">
                Learn more about {current.name} <ArrowRight className="h-4 w-4" />
              </Link>
              <div className="mt-4 flex">
                {items.map((p, i) => (
                  <button
                    key={p.slug}
                    onClick={() => setActive(i)}
                    aria-label={`Show ${p.name}`}
                    className="h-11 min-w-11 px-1 inline-flex items-center justify-center"
                  >
                    <span className={`h-1.5 rounded-full transition-all ${i === active ? "w-8 bg-primary" : "w-3 bg-border"}`} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="container mt-16">
        <div className="relative mx-auto max-w-5xl overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-card)]">
          {open ? (
            <video src={suiteVideo.url} poster={poster} controls autoPlay playsInline className="w-full aspect-video bg-foreground" />
          ) : (
            <button
              onClick={() => {
                setOpen(true);
                (window as unknown as { umami?: { track: (e: string) => void } }).umami?.track("suite-video-play");
              }}
              className="group relative block w-full aspect-video"
              aria-label="Play the Bane Tech ecosystem video"
            >
              <img src={poster} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              <span className="absolute inset-0 bg-foreground/20 group-hover:bg-foreground/10 transition-colors" />
              <span className="absolute inset-0 grid place-items-center">
                <span className="grid place-items-center h-20 w-20 rounded-full bg-primary text-primary-foreground shadow-[var(--shadow-card)] group-hover:scale-110 transition-transform">
                  <Play className="h-8 w-8 ml-1" />
                </span>
              </span>
              <span className="absolute bottom-4 left-4 rounded-full bg-card/90 px-4 py-1.5 text-sm font-medium text-foreground">
                Watch the ecosystem · 1 min
              </span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
